/**
 * Status-aware header card for the Converted Lead detail page.
 *
 * Renders a different summary block per lifecycle status. Confirmed shows
 * the day-of-confirmation + actions taken; Exhausted shows the last quote
 * and "no confirmation"; In Progress shows the current day + next-check
 * estimate; Scheduled shows the day-0 placeholder; Cancelled / Failed show
 * the error/cancel reason.
 */

import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { CheckCircle2, Circle, MinusCircle, XCircle } from 'lucide-react'
import {
  computeFieldWriteTrace,
  formatCancelReason,
  formatCurrency,
  getConvertedStatusColor,
  getFieldWriteStatusDisplay,
  truncateId,
  type FieldWriteTraceState,
  type FieldWriteTraceStep,
} from '@/lib/ghl-qualifier'
import { convertedCheckDay } from '@/lib/ghl-qualifier-schedule'
import type { ConvertedLeadDetail } from '@/hooks/useGHLQualifierDashboard'

/**
 * Icon + color treatment per trace-step state.
 */
function traceStateVisuals(state: FieldWriteTraceState): {
  Icon: typeof CheckCircle2
  iconClass: string
  labelClass: string
  detailClass: string
} {
  switch (state) {
    case 'passed':
      return {
        Icon: CheckCircle2,
        iconClass: 'text-green-600',
        labelClass: 'text-foreground font-medium',
        detailClass: 'text-muted-foreground',
      }
    case 'failed':
      return {
        Icon: XCircle,
        iconClass: 'text-red-600',
        labelClass: 'text-red-900 font-medium',
        detailClass: 'text-red-800',
      }
    case 'skipped_block':
      return {
        Icon: MinusCircle,
        iconClass: 'text-amber-600',
        labelClass: 'text-amber-900 font-medium',
        detailClass: 'text-amber-800',
      }
    case 'not_evaluated':
      return {
        Icon: Circle,
        iconClass: 'text-muted-foreground/40',
        labelClass: 'text-muted-foreground/60',
        detailClass: 'text-muted-foreground/50',
      }
  }
}

/**
 * Render a single step of the field-write decision trace.
 */
function FieldWriteTraceStepRow({ step, index }: { step: FieldWriteTraceStep; index: number }) {
  const { Icon, iconClass, labelClass, detailClass } = traceStateVisuals(step.state)
  return (
    <li className="flex items-start gap-2">
      <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${iconClass}`} aria-hidden="true" />
      <div className="flex-1 min-w-0">
        <div className={`text-sm ${labelClass}`}>
          <span className="text-xs text-muted-foreground/70 mr-1.5">Step {index + 1}:</span>
          {step.label}
        </div>
        {step.detail && (
          <div className={`text-xs mt-0.5 whitespace-pre-wrap break-words ${detailClass}`}>
            {step.detail}
          </div>
        )}
      </div>
    </li>
  )
}

/**
 * Step-by-step trace of the field-write decision tree. Mirrors the `[FIELD-WRITE]`
 * Railway logs so operators can diagnose every confirmed-conversion lifecycle
 * from the dashboard alone — no log-grepping required for the common cases.
 *
 * Returns null when the lifecycle never reached the field-write code path
 * (e.g. tag-pre-check short-circuit, in-progress, or pre-deploy rows).
 */
function FieldWriteTraceBlock({ lifecycle }: { lifecycle: ConvertedLeadDetail }) {
  const trace = computeFieldWriteTrace(
    lifecycle.field_write_status,
    lifecycle.field_write_error,
    lifecycle.last_price_quoted,
    lifecycle.monetary_value_written
  )
  if (!trace) return null

  return (
    <div className="mt-3 rounded-md border border-border bg-card px-3 py-3">
      <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
        Field-write decision trace
      </div>
      <ol className="space-y-2.5">
        {trace.map((step, i) => (
          <FieldWriteTraceStepRow key={step.key} step={step} index={i} />
        ))}
      </ol>
    </div>
  )
}

/**
 * Inline block summarising the contact.google_ads_converted_leads_monetary field-write
 * attempt. Rendered only when the lifecycle reached confirmed-conversion AND
 * the worker actually attempted the write (i.e. field_write_status is not
 * null). Tag-pre-check early-exits leave it null and we render nothing.
 */
function FieldWriteStatusBlock({ lifecycle }: { lifecycle: ConvertedLeadDetail }) {
  const display = getFieldWriteStatusDisplay(lifecycle.field_write_status)
  if (!display) return null

  const Icon =
    display.icon === 'check'
      ? CheckCircle2
      : display.icon === 'skip'
        ? MinusCircle
        : XCircle

  return (
    <div
      className={`mt-3 rounded-md border ${display.border} ${display.bg} px-3 py-2`}
      role="status"
      aria-label={`Google Ads conversion field write: ${display.label}`}
    >
      <div className="flex items-start gap-2">
        <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${display.text}`} />
        <div className="flex-1 min-w-0">
          <div className={`text-sm font-medium ${display.text}`}>
            Google Ads conversion field — {display.label}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            <code className="font-mono text-[11px]">contact.google_ads_converted_leads_monetary</code>
            {lifecycle.field_write_attempted_at && (
              <span className="ml-2">
                · {new Date(lifecycle.field_write_attempted_at).toLocaleString()}
              </span>
            )}
          </div>
          {lifecycle.field_write_error && (
            <div className={`mt-1.5 text-xs ${display.text} whitespace-pre-wrap break-words`}>
              {lifecycle.field_write_error}
            </div>
          )}
          {display.severity === 'error' &&
            lifecycle.field_write_status === 'failed_field_missing' && (
              <div className="mt-1.5 text-xs text-red-900">
                <span className="font-medium">To fix:</span> create a custom field on
                this location with the field key{' '}
                <code className="font-mono text-[11px]">contact.google_ads_converted_leads_monetary</code>{' '}
                in GHL → Settings → Custom Fields → Contact.
              </div>
            )}
        </div>
      </div>
    </div>
  )
}

interface Props {
  readonly lifecycle: ConvertedLeadDetail
}

function calculateNextCheck(lifecycle: ConvertedLeadDetail): Date | null {
  // The drain writes the exact time of the next check; gaps vary (daily for
  // the first 8 days, then days 15/22/30/60), so read it rather than guess.
  return lifecycle.next_fire_at ? new Date(lifecycle.next_fire_at) : null
}

export function LifecycleHeader({ lifecycle }: Props) {
  const status = getConvertedStatusColor(lifecycle.status)

  if (lifecycle.status === 'completed_converted') {
    return (
      <Card className="border-green-200 bg-green-50/40">
        <CardContent className="pt-6 space-y-2">
          <div className="flex items-center gap-2">
            <Badge className={`${status.bg} ${status.text} hover:${status.bg} text-sm`}>
              Confirmed on Day {convertedCheckDay(lifecycle.check_count)}
            </Badge>
          </div>
          <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
            {lifecycle.last_price_quoted != null && (
              <>
                <dt className="text-muted-foreground">Final price quoted</dt>
                <dd className="font-medium">{formatCurrency(lifecycle.last_price_quoted)}</dd>
              </>
            )}
            {lifecycle.monetary_value_written != null && (
              <>
                <dt className="text-muted-foreground">$ Written to GHL</dt>
                <dd className="font-medium">
                  {formatCurrency(lifecycle.monetary_value_written)}
                  {lifecycle.opportunity_id && (
                    <span className="ml-2 font-mono text-xs text-muted-foreground">
                      {truncateId(lifecycle.opportunity_id, 14)}
                    </span>
                  )}
                  {lifecycle.monetary_value_written_at && (
                    <span className="ml-2 text-xs text-muted-foreground">
                      {new Date(lifecycle.monetary_value_written_at).toLocaleString()}
                    </span>
                  )}
                </dd>
              </>
            )}
            {/*
              error_message is intentionally NOT rendered here anymore — its
              prior usage (storing field_already_populated / value_write_failed)
              has been replaced by the discriminated FieldWriteStatusBlock
              below, which gives operators a clearer picture.
            */}
          </dl>
          <FieldWriteStatusBlock lifecycle={lifecycle} />
          <FieldWriteTraceBlock lifecycle={lifecycle} />
        </CardContent>
      </Card>
    )
  }

  if (lifecycle.status === 'completed_exhausted') {
    return (
      <Card>
        <CardContent className="pt-6 space-y-2">
          <Badge className={`${status.bg} ${status.text} hover:${status.bg} text-sm`}>
            Exhausted after {lifecycle.max_checks} checks (Day{' '}
            {convertedCheckDay(lifecycle.max_checks)})
          </Badge>
          <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
            {lifecycle.last_price_quoted != null && (
              <>
                <dt className="text-muted-foreground">Last price quoted</dt>
                <dd className="font-medium">{formatCurrency(lifecycle.last_price_quoted)}</dd>
              </>
            )}
            <dt className="text-muted-foreground">Outcome</dt>
            <dd>No booking confirmation detected.</dd>
          </dl>
        </CardContent>
      </Card>
    )
  }

  // `pending` and `processing` are transient between-check states the drain
  // writes — same UX as `in_progress` (active lifecycle, more checks coming).
  if (
    lifecycle.status === 'in_progress' ||
    lifecycle.status === 'scheduled' ||
    lifecycle.status === 'pending' ||
    lifecycle.status === 'processing'
  ) {
    const next = calculateNextCheck(lifecycle)
    return (
      <Card>
        <CardContent className="pt-6 space-y-2">
          <Badge className={`${status.bg} ${status.text} hover:${status.bg} text-sm`}>
            {status.label} — Check {lifecycle.check_count}/{lifecycle.max_checks}
            {lifecycle.check_count > 0 && ` (Day ${convertedCheckDay(lifecycle.check_count)})`}
          </Badge>
          <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
            {lifecycle.last_price_quoted != null && (
              <>
                <dt className="text-muted-foreground">Last price quoted</dt>
                <dd className="font-medium">{formatCurrency(lifecycle.last_price_quoted)}</dd>
              </>
            )}
            {next && (
              <>
                <dt className="text-muted-foreground">Next check</dt>
                <dd>
                  <time dateTime={next.toISOString()}>{next.toLocaleString()}</time>
                </dd>
              </>
            )}
          </dl>
        </CardContent>
      </Card>
    )
  }

  if (lifecycle.status === 'cancelled') {
    return (
      <Card>
        <CardContent className="pt-6 space-y-2">
          <Badge className={`${status.bg} ${status.text} hover:${status.bg} text-sm`}>
            Cancelled
          </Badge>
          <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
            <dt className="text-muted-foreground">Reason</dt>
            <dd>{formatCancelReason(lifecycle.cancel_reason)}</dd>
            <dt className="text-muted-foreground">Cancelled at</dt>
            <dd>
              <time dateTime={lifecycle.updated_at}>
                {new Date(lifecycle.updated_at).toLocaleString()}
              </time>
            </dd>
          </dl>
        </CardContent>
      </Card>
    )
  }

  // Failed / unknown
  return (
    <Card>
      <CardContent className="pt-6 space-y-2">
        <Badge className={`${status.bg} ${status.text} hover:${status.bg} text-sm`}>
          {status.label}
        </Badge>
        {lifecycle.error_message ? (
          <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-900">
            <strong>Error:</strong> {lifecycle.error_message}
          </div>
        ) : null}
        <p className="text-xs text-muted-foreground">
          Last evaluated:{' '}
          {lifecycle.last_evaluated_at
            ? new Date(lifecycle.last_evaluated_at).toLocaleString()
            : '—'}
        </p>
      </CardContent>
    </Card>
  )
}
